import { __awaiter } from "tslib";
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { nanoid } from 'nanoid';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { animated, Globals, useSpring, useTransition } from 'react-spring';
import { styled } from 'styled-components';
import { onEnterOrSpacePress } from '@hivebrite/utils';
import { Box } from "../../box/src";
import { CATEGORIES, FontAwesomeIcon } from "../../font-awesome-icon/src";
import { useTheme } from "../../theme/src";
const AnimatedBox = animated(Box);
export const AccordionSimple = ({ buttonTag = 'button', children, dataTestId, immediateAnimation = false, isOpen = false, onToggle, title, titlePosition = 'top', titleTag = 'h2', triggerSticky = false, triggerStyle, }) => {
    const nodeRef = useRef(null);
    const immediateAnimationRef = useRef(isOpen);
    const titleId = useMemo(() => nanoid(), []);
    const contentId = useMemo(() => nanoid(), []);
    const immediate = Globals.skipAnimation ||
        immediateAnimationRef.current ||
        immediateAnimation;
    const handleToggle = useCallback((e) => {
        e.preventDefault();
        onToggle === null || onToggle === void 0 ? void 0 : onToggle(!isOpen);
    }, [isOpen, onToggle]);
    const transitionsContent = useTransition(isOpen, {
        enter: () => (next) => __awaiter(void 0, void 0, void 0, function* () {
            if (nodeRef.current) {
                const { height } = nodeRef.current.getBoundingClientRect();
                yield next({
                    height,
                    immediate,
                    opacity: 1,
                    pointerEvents: 'all',
                });
            }
        }),
        from: {
            height: immediate ? 'auto' : 0,
            immediate,
            opacity: immediate ? 1 : 0,
            pointerEvents: immediate ? 'all' : 'none',
        },
        leave: {
            height: 0,
            immediate,
            opacity: 0,
            pointerEvents: 'none',
        },
        update: () => (next) => __awaiter(void 0, void 0, void 0, function* () {
            if (nodeRef.current) {
                const { height } = nodeRef.current.getBoundingClientRect();
                yield next({
                    height,
                    immediate,
                    opacity: 1,
                    pointerEvents: 'all',
                });
            }
        }),
    });
    useEffect(() => {
        immediateAnimationRef.current = false;
    }, []);
    const titleComponent = title ? (_jsx(Box, { as: titleTag, display: "block", m: 0, style: Object.assign(Object.assign({}, (triggerSticky
            ? {
                position: isOpen ? 'sticky' : undefined,
                top: 0,
                zIndex: 1,
            }
            : {})), triggerStyle), children: _jsx(Trigger, { "aria-controls": contentId, "aria-expanded": isOpen, as: buttonTag, "data-testid": dataTestId, id: titleId, onClick: handleToggle, onKeyDown: onEnterOrSpacePress(handleToggle), role: "button", tabIndex: 0, children: typeof title === 'function' ? title({ isOpen }) : title }) })) : null;
    return (_jsxs(_Fragment, { children: [titlePosition !== 'bottom' && titleComponent, transitionsContent((styles, item) => {
                var _a, _b;
                const animationStyleProps = {
                    height: (_a = styles.height) === null || _a === void 0 ? void 0 : _a.to((value) => value < styles.height.animation.to ? value : null),
                    opacity: styles.opacity,
                    overflow: (_b = styles.opacity) === null || _b === void 0 ? void 0 : _b.to((value) => value >= 0.99 ? undefined : 'hidden'),
                    pointerEvents: styles.pointerEvents,
                };
                return (item && (_jsx(animated.div, { "aria-hidden": !isOpen, "aria-labelledby": titleId, id: contentId, role: "region", style: animationStyleProps, children: typeof children === 'function'
                        ? children({
                            isOpen,
                            ref: nodeRef,
                        })
                        : children })));
            }), titlePosition === 'bottom' && titleComponent] }));
};
export const Accordion = ({ children, dataTestId, isOpen = false, onToggle, title, triggerSticky = false, triggerStyle, }) => {
    const theme = useTheme();
    const springTitle = useSpring({
        bg: isOpen ? '#ebf1fd' : '#f3f3f3',
        color: isOpen
            ? theme.colors.primary
            : theme.colors.paleSky || theme.colors.dove,
        rotate: isOpen ? 180 : 0,
    });
    return (_jsx(AccordionSimple, { dataTestId: dataTestId, isOpen: isOpen, onToggle: onToggle, title: () => (_jsxs(TitleStyled, { style: {
                backgroundColor: springTitle.bg,
                color: springTitle.color,
            }, children: [_jsx(Box, { flex: 1, fontSize: 4, fontWeight: 1, style: { color: 'inherit' }, children: title }), _jsx(AnimatedBox, { color: "inherit", style: {
                        transform: springTitle.rotate.to((rotate) => `rotate(${rotate}deg)`),
                    }, children: _jsx(FontAwesomeIcon, { height: "20px", iconName: "angle-down", iconType: CATEGORIES.LIGHT }) })] })), triggerSticky: triggerSticky, triggerStyle: triggerStyle, children: children }));
};
const Trigger = styled(Box).attrs(() => ({
    alignItems: 'center',
    color: 'asphalt',
    type: 'button',
})) `
  background: transparent;
  border: 0;
  cursor: pointer;
  display: block;
  font-family: inherit;
  margin: 0;
  padding: 0;
  text-align: left;
  user-select: none;
  width: 100%;

  &:focus {
    outline-width: 5px;
  }
`;
const TitleStyled = styled(AnimatedBox).attrs(() => ({
    alignItems: 'center',
    height: 50,
    mb: '10px',
    px: 6,
})) `
  cursor: pointer;
  user-select: none;
`;
//# sourceMappingURL=Accordion.js.map